/* Moments Etched v7.6.0 — Premium Builder Experience.
 * Additive customer-experience layer. Core cart, upload, pricing and CSV logic remain untouched.
 */
(() => {
  'use strict';

  const STORAGE_PREFIX = 'moments-builder-draft:';
  const STEP_ORDER = ['design', 'artwork', 'review'];
  const SUCCESS_WORDS = /complete|added|ready|uploaded|success|saved/i;
  const ERROR_WORDS = /required|select|upload|check|error|unable|please|missing|choose/i;

  const debounce = (fn, wait = 180) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const announce = (root, message, type = 'info') => {
    let toast = root.querySelector('[data-me-premium-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'me-premium-toast';
      toast.dataset.mePremiumToast = '';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      root.querySelector('.me-designer__dialog')?.appendChild(toast);
    }
    toast.textContent = message;
    toast.dataset.type = type;
    toast.classList.add('is-visible');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
  };

  const pulse = (node) => {
    if (!node) return;
    node.classList.remove('me-value-updated');
    void node.offsetWidth;
    node.classList.add('me-value-updated');
  };

  function enhanceArtworkInputs(root) {
    root.querySelectorAll('input[type="file"]').forEach((input, index) => {
      if (input.dataset.meV760FileReady === 'true') return;
      input.dataset.meV760FileReady = 'true';

      const shell = document.createElement('div');
      shell.className = 'me-artwork-readiness';
      shell.dataset.meArtworkReadiness = '';
      shell.innerHTML = `
        <div class="me-artwork-readiness__icon" aria-hidden="true">↑</div>
        <div class="me-artwork-readiness__copy">
          <strong>Choose production artwork</strong>
          <span>Transparent PNG is preferred. High-resolution PDF, SVG, JPG and JPEG files are also accepted.</span>
          <small data-me-artwork-file-status>No file selected</small>
        </div>`;
      input.parentNode?.insertBefore(shell, input);

      const status = shell.querySelector('[data-me-artwork-file-status]');
      input.setAttribute('aria-describedby', `me-file-help-${index}`);
      status.id = `me-file-help-${index}`;

      input.addEventListener('change', () => {
        const file = input.files?.[0];
        shell.classList.toggle('has-file', Boolean(file));
        if (!file) {
          status.textContent = 'No file selected';
          return;
        }
        const sizeMb = file.size / (1024 * 1024);
        status.textContent = `✓ ${file.name} · ${sizeMb.toFixed(sizeMb >= 10 ? 0 : 1)} MB`;
        announce(root, `Artwork selected: ${file.name}`, 'success');
      });
    });
  }

  function setupLiveSummary(root) {
    const watched = root.querySelectorAll(
      '[data-me-project-color], [data-me-project-size], [data-me-project-quantity], [data-me-project-locations], [data-me-project-total], [data-me-price-total], [data-me-price-unit]'
    );
    watched.forEach((node) => {
      let previous = node.textContent.trim();
      new MutationObserver(() => {
        const current = node.textContent.trim();
        if (current && current !== previous) {
          previous = current;
          pulse(node);
        }
      }).observe(node, {childList: true, characterData: true, subtree: true});
    });
  }

  function storageKey(root) {
    return `${STORAGE_PREFIX}${root.dataset.meProductHandle || location.pathname}`;
  }

  function serializeDraft(root) {
    const controls = [];
    root.querySelectorAll('input, select, textarea').forEach((field, index) => {
      if (!field.name && !field.dataset.meSizeQuantity && !field.dataset.meOrderMode) return;
      if (field.type === 'file' || field.type === 'hidden' || field.disabled) return;
      const identity = field.name || field.id || field.dataset.meSizeValue || field.dataset.meOrderMode || `field-${index}`;
      controls.push({
        identity,
        type: field.type,
        value: field.value,
        checked: Boolean(field.checked)
      });
    });
    return {
      version: '7.6.0',
      savedAt: Date.now(),
      controls
    };
  }

  function saveDraft(root, quiet = true) {
    try {
      localStorage.setItem(storageKey(root), JSON.stringify(serializeDraft(root)));
      const indicator = root.querySelector('[data-me-autosave-status]');
      if (indicator) {
        indicator.textContent = 'Selections saved on this device';
        indicator.classList.add('is-saved');
        setTimeout(() => indicator.classList.remove('is-saved'), 1400);
      }
      if (!quiet) announce(root, 'Your selections were saved on this device.', 'success');
    } catch (error) {
      console.warn('[Moments v7.6.0] Draft could not be saved.', error);
    }
  }

  function addAutosaveIndicator(root) {
    const brand = root.querySelector('.me-designer__brand');
    if (!brand || brand.querySelector('[data-me-autosave-status]')) return;
    const indicator = document.createElement('span');
    indicator.className = 'me-autosave-status';
    indicator.dataset.meAutosaveStatus = '';
    indicator.textContent = 'Selections save automatically';
    brand.appendChild(indicator);
  }

  function addGuidance(root) {
    root.querySelectorAll('[data-me-step-panel]').forEach((panel) => {
      if (panel.querySelector('[data-me-step-guidance]')) return;
      const key = panel.dataset.meStepPanel;
      const messages = {
        design: ['Start with the essentials', 'Choose a color and order type, then enter the size quantities needed for your project.'],
        artwork: ['Artwork stays connected to your project', 'Upload and assign each file to its intended print location. Your files are reviewed before production.'],
        review: ['One final check', 'Confirm quantities, artwork assignments, pricing and authorization before adding the project to your cart.']
      };
      const [title, body] = messages[key] || messages.design;
      const callout = document.createElement('div');
      callout.className = 'me-step-guidance';
      callout.dataset.meStepGuidance = '';
      callout.innerHTML = `<span aria-hidden="true">${STEP_ORDER.indexOf(key) + 1}</span><div><strong>${title}</strong><p>${body}</p></div>`;
      panel.querySelector('.me-designer-panel__heading')?.insertAdjacentElement('afterend', callout);
    });
  }

  function setupStatusFeedback(root) {
    const status = root.querySelector('[data-me-designer-cart-status]');
    if (!status) return;
    let previous = '';
    new MutationObserver(() => {
      const message = status.textContent.trim();
      if (!message || message === previous) return;
      previous = message;
      if (ERROR_WORDS.test(message)) announce(root, message, 'error');
      else if (SUCCESS_WORDS.test(message)) announce(root, message, 'success');
    }).observe(status, {childList: true, characterData: true, subtree: true});
  }

  function setupStepFocus(root) {
    const buttons = [...root.querySelectorAll('[data-me-step-button]')];
    const update = () => {
      const active = buttons.find((button) => button.classList.contains('is-active') || button.getAttribute('aria-current') === 'step');
      const index = Math.max(0, STEP_ORDER.indexOf(active?.dataset.meStepButton || 'design'));
      root.dataset.meActiveStep = STEP_ORDER[index];
      buttons.forEach((button, buttonIndex) => {
        button.setAttribute('aria-label', `${buttonIndex < index ? 'Completed: ' : buttonIndex === index ? 'Current step: ' : ''}${button.textContent.trim().replace(/\s+/g, ' ')}`);
      });
    };
    buttons.forEach((button) => button.addEventListener('click', () => requestAnimationFrame(update)));
    root.querySelector('[data-me-designer-primary-action]')?.addEventListener('click', () => requestAnimationFrame(update));
    update();
  }

  function init(root) {
    if (!root || root.dataset.meV760Ready === 'true') return;
    root.dataset.meV760Ready = 'true';
    root.classList.add('me-designer--premium');

    addAutosaveIndicator(root);
    addGuidance(root);
    enhanceArtworkInputs(root);
    setupLiveSummary(root);
    setupStatusFeedback(root);
    setupStepFocus(root);

    const autoSave = debounce(() => saveDraft(root, true), 280);
    root.addEventListener('change', (event) => {
      if (event.target?.matches?.('input:not([type="file"]), select, textarea')) autoSave();
    });
    root.addEventListener('input', (event) => {
      if (event.target?.matches?.('input[type="number"], input[type="text"], textarea')) autoSave();
    });

    const openButton = document.querySelector(`[data-me-designer-open][data-section-id="${root.closest('[data-section-id]')?.dataset.sectionId || ''}"]`) || document.querySelector('[data-me-designer-open]');
    openButton?.addEventListener('click', () => {
      const saved = localStorage.getItem(storageKey(root));
      if (saved) {
        try {
          const draft = JSON.parse(saved);
          if (draft?.savedAt && Date.now() - draft.savedAt < 1000 * 60 * 60 * 24 * 14) {
            announce(root, 'Your recent selections are saved on this device. Artwork files must be selected again for security.', 'info');
          }
        } catch (_) {}
      }
    });

    root.querySelector('[data-me-designer-primary-action]')?.addEventListener('click', () => saveDraft(root, true));
  }

  const boot = () => document.querySelectorAll('[data-me-designer]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', boot) : boot();
  new MutationObserver(boot).observe(document.documentElement, {childList: true, subtree: true});
})();
